import React, { useRef } from 'react';
import MetricComparisonCard from './MetricComparisonCard';
import MetricComparisonCardSkeleton from './MetricComparisonCardSkeleton';
import type { MetricComparisonCardData } from '@app-types/comparisonMetric';
import { useCompanyNameLineCount } from './useCompanyNameLineCount';

export interface MetricComparisonCardListProps {
  cards: MetricComparisonCardData[];
  nodeId?: string;
  /** When true, render `placeholderCount` skeleton cards instead of `cards`. */
  isLoading?: boolean;
  /** Number of skeleton cards to render while loading. */
  placeholderCount?: number;
}

/**
 * List wrapper for the 10 metric comparison cards. Layout classes live in
 * ComparisonPage.less so the page owns the row-gap between cards.
 * Figma: web parent 3226:16335 etc. (single-column list of horizontal cards).
 */
const MetricComparisonCardList: React.FC<MetricComparisonCardListProps> = ({
  cards,
  nodeId,
  isLoading = false,
  placeholderCount = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const nameLines = useCompanyNameLineCount(containerRef, cards, isLoading);
  return (
    <div
      ref={containerRef}
      className="comparison-metric-list"
      data-company-name-lines={nameLines}
      data-node-id={nodeId}
      aria-busy={isLoading}
    >
      {isLoading
        ? Array.from({ length: placeholderCount }, (_, index) => (
          <MetricComparisonCardSkeleton key={`comparison-metric-skeleton-${index}`} />
        ))
        : cards.map((card) => (
          <MetricComparisonCard key={card.key} data={card} />
        ))}
    </div>
  );
};

export default MetricComparisonCardList;
